import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, X, Copy, Check } from "lucide-react";
import { routersApi, onboardingApi } from "../api/client.ts";
import { useAuth } from "../lib/auth.tsx";
import type { CreateRouterResponse, RouterConfigResponse } from "../lib/types.ts";

export function RoutersPage() {
  const { tenant } = useAuth();
  const auth = { tenantId: tenant!.tenantId, idToken: tenant!.idToken };
  const [showWizard, setShowWizard] = useState(false);

  const list = useQuery({
    queryKey: ["routers", auth.tenantId],
    queryFn: () => routersApi.list(auth),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Routers</h2>
          <p className="text-sm text-slate-500">
            Register your MikroTiks and generate paste-ready RouterOS configs.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowWizard((s) => !s)}
          className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          {showWizard ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showWizard ? "Close wizard" : "Add router"}
        </button>
      </div>

      {showWizard && <OnboardingWizard onClose={() => setShowWizard(false)} />}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">NAS-Identifier</th>
              <th className="px-4 py-3 font-medium">Subdomain</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {list.isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No routers yet. Click <strong>Add router</strong>.
                </td>
              </tr>
            )}
            {list.data?.map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">
                  {r.nasIdentifier}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {r.hotspotSubdomain ?? "—"}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={r.status} />
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {r.lastSeenAt ? new Date(r.lastSeenAt).toLocaleString() : "never"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "online"
      ? "bg-brand-50 text-brand-700"
      : status === "pending"
        ? "bg-amber-50 text-amber-700"
        : status === "offline"
          ? "bg-red-50 text-red-700"
          : "bg-slate-100 text-slate-500";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {status}
    </span>
  );
}

// ---- Wizard ----

const Step1Schema = z.object({
  name: z.string().min(1).max(80),
  hotspotSubdomain: z
    .string()
    .regex(/^[a-z0-9-]+$/, "lowercase, digits, dashes only")
    .min(2)
    .max(40)
    .optional()
    .or(z.literal("").transform(() => undefined)),
});
type Step1Values = z.infer<typeof Step1Schema>;

const Step2Schema = z.object({
  routerOsVersion: z.enum(["6", "7"]),
  hotspotInterface: z.string().min(1),
  hotspotBridge: z.string().optional(),
  wanInterface: z.string().optional(),
  hotspotSubnet: z
    .string()
    .regex(/^\d+\.\d+\.\d+\.\d+\/\d+$/)
    .optional()
    .or(z.literal("").transform(() => undefined)),
});
type Step2Values = z.infer<typeof Step2Schema>;

function OnboardingWizard({ onClose }: { onClose: () => void }) {
  const { tenant } = useAuth();
  const auth = { tenantId: tenant!.tenantId, idToken: tenant!.idToken };
  const qc = useQueryClient();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [router, setRouter] = useState<CreateRouterResponse | null>(null);
  const [configResult, setConfigResult] = useState<RouterConfigResponse | null>(
    null,
  );

  const createRouter = useMutation({
    mutationFn: (values: Step1Values) => routersApi.create(auth, values),
    onSuccess: (r) => {
      setRouter(r);
      setStep(2);
      qc.invalidateQueries({ queryKey: ["routers", auth.tenantId] });
    },
  });

  const generateConfig = useMutation({
    mutationFn: (values: Step2Values) =>
      onboardingApi.routerConfig(auth, router!.id, values),
    onSuccess: (r) => {
      setConfigResult(r);
      setStep(3);
    },
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="mb-6 flex items-center gap-3 text-xs">
        <StepBadge n={1} active={step === 1} done={step > 1} label="Identity" />
        <span className="h-px flex-1 bg-slate-200" />
        <StepBadge n={2} active={step === 2} done={step > 2} label="Network" />
        <span className="h-px flex-1 bg-slate-200" />
        <StepBadge n={3} active={step === 3} done={false} label="Script" />
      </div>

      {step === 1 && (
        <Step1Form
          submitting={createRouter.isPending}
          error={createRouter.error?.message}
          onSubmit={(v) => createRouter.mutate(v)}
        />
      )}

      {step === 2 && router && (
        <Step2Form
          router={router}
          submitting={generateConfig.isPending}
          error={generateConfig.error?.message}
          onSubmit={(v) => generateConfig.mutate(v)}
        />
      )}

      {step === 3 && configResult && (
        <Step3Script result={configResult} onClose={onClose} />
      )}
    </div>
  );
}

function StepBadge({
  n,
  active,
  done,
  label,
}: {
  n: number;
  active: boolean;
  done: boolean;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${
          done
            ? "bg-brand-600 text-white"
            : active
              ? "bg-brand-100 text-brand-700 ring-2 ring-brand-600"
              : "bg-slate-100 text-slate-500"
        }`}
      >
        {done ? "✓" : n}
      </span>
      <span
        className={`text-xs font-medium ${active ? "text-slate-900" : "text-slate-500"}`}
      >
        {label}
      </span>
    </div>
  );
}

function Step1Form({
  onSubmit,
  submitting,
  error,
}: {
  onSubmit: (v: Step1Values) => void;
  submitting: boolean;
  error?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Step1Values>({ resolver: zodResolver(Step1Schema) });
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 sm:grid-cols-2">
      <Field label="Router name" error={errors.name?.message}>
        <input
          {...register("name")}
          placeholder="e.g. Kibanda 1"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </Field>
      <Field
        label="Captive portal subdomain (optional)"
        error={errors.hotspotSubdomain?.message}
      >
        <div className="flex items-stretch">
          <input
            {...register("hotspotSubdomain")}
            placeholder="kibanda-1"
            className="w-full rounded-l-md border border-r-0 border-slate-300 px-3 py-2 text-sm"
          />
          <span className="rounded-r-md border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500">
            .portal.spotmesh.cloud
          </span>
        </div>
      </Field>
      {error && <p className="sm:col-span-2 text-sm text-red-600">{error}</p>}
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {submitting ? "Creating…" : "Continue →"}
        </button>
      </div>
    </form>
  );
}

function Step2Form({
  router,
  onSubmit,
  submitting,
  error,
}: {
  router: CreateRouterResponse;
  onSubmit: (v: Step2Values) => void;
  submitting: boolean;
  error?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Step2Values>({
    resolver: zodResolver(Step2Schema),
    defaultValues: {
      routerOsVersion: "7",
      hotspotInterface: "wlan1",
      hotspotBridge: "bridge-hotspot",
      wanInterface: "ether1",
      hotspotSubnet: "10.55.50.0/24",
    },
  });
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="rounded-md bg-slate-50 p-4 text-sm">
        <p className="text-slate-600">
          Router <span className="font-medium text-slate-900">{router.name}</span>{" "}
          created.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          NAS-Identifier:{" "}
          <code className="rounded bg-white px-1.5 py-0.5 font-mono">
            {router.nasIdentifier}
          </code>
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="RouterOS version" error={errors.routerOsVersion?.message}>
          <select
            {...register("routerOsVersion")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="7">RouterOS 7</option>
            <option value="6">RouterOS 6</option>
          </select>
        </Field>
        <Field label="Hotspot interface" error={errors.hotspotInterface?.message}>
          <input
            {...register("hotspotInterface")}
            placeholder="wlan1 or ether2"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Hotspot bridge" error={errors.hotspotBridge?.message}>
          <input
            {...register("hotspotBridge")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="WAN interface" error={errors.wanInterface?.message}>
          <input
            {...register("wanInterface")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Hotspot subnet" error={errors.hotspotSubnet?.message}>
          <input
            {...register("hotspotSubnet")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {submitting ? "Generating…" : "Generate script →"}
        </button>
      </div>
    </form>
  );
}

function Step3Script({
  result,
  onClose,
}: {
  result: RouterConfigResponse;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(result.script);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-900">
            Paste this into your MikroTik's terminal.
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Treat the script as secret — it contains your RADIUS shared key.
          </p>
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-xs font-medium hover:bg-slate-50"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="max-h-96 overflow-auto rounded-md border border-slate-200 bg-slate-900 p-4 text-xs leading-relaxed text-slate-100">
        {result.script}
      </pre>
      <div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <div className="mt-1">{children}</div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </label>
  );
}
